import React, { Component } from 'react';
import { ModalBody, ModalFooter, FormGroup, Label, Input, FormText } from 'reactstrap';
import { APIClient } from '../../../utils/APIClient';
import { API_URL, TIKTOK_APP_ID, APP_URL, LINKEDIN_AUTHORIZATION_URL } from '../../../settings/local_settings';
import { FaIcon } from '../../FaIcon';
import { withTranslation } from 'react-i18next';
import { MERCADO_ADS_CHANNEL } from '../../channels/channels';
import {
    ERROR_KIND_ACCOUNT_DISABLED, ERROR_KIND_FB_SECURITY_CHECK, ERROR_KIND_GENERIC, ERROR_KIND_PERMISSIONS,
    resolveErrorKind,
} from '../../channels/adAccountErrorKinds';
import { setPendingLinkedinConnection } from './pendingLinkedinReconnect';
import { forgetReconnect, rememberReconnect } from './reconnectNote';
import {
    formatShortDate, isPaidReconnectChannel, ownerNamesPerson, ownerSentence, reconnectPlatformName,
    recordReconnectEvent,
} from './reconnectAccountText';

const STEP_SELECT = 'SELECT';
const STEP_ERROR = 'ERROR';
const RAW = {escapeValue: false};

// «Dashbo dejó de ver esta cuenta…»: los motivos que son eso mismo. Los demás (la conexión se cortó, la
// cuenta está deshabilitada, el checkpoint de Meta) dicen su propio motivo.
const LOST_ACCESS_KINDS = [ERROR_KIND_PERMISSIONS, ERROR_KIND_GENERIC];

const OAUTH_AUTH_URL_BY_CHANNEL = {
    GOOGLE: 'get_auth_url/',
    ANALYTICS: 'get_auth_url/ga4/',
    DV360: 'get_auth_url/dv360/',
};

/**
 * El «Reconectar» de UNA cuenta caída (M2 de la tanda «Reconexión de pauta», UD-20).
 *
 * En los cinco canales de pauta dice qué pasó y quién ve la cuenta (copy §1), qué hacer, y pide el
 * nombre de la conexión como OPCIONAL: el nombre obligatorio era lo que hacía rebotar con
 * `*_USER_ALREADY_CONNECTED` a quien entraba con el mismo usuario. Al irse a la plataforma deja anotada
 * la cuenta con su canal (`reconnectNote`), y el callback de ese canal se la manda al backend como
 * `reconnect_ad_account_id`: con eso renueva la conexión del mismo usuario o pasa la cuenta a la del
 * que entró.
 *
 * GA4 comparte este modal pero queda como antes (P-4 de la tanda): nombre obligatorio y su
 * descripción. Mercado Ads se resuelve en Conexiones.
 */
class PaidMediaReconnectFlowClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            step: STEP_SELECT,
            credentialName: '',
            errorMessage: null,
        };
        this.handleConnectNew = this.handleConnectNew.bind(this);
        this.handleRetry = this.handleRetry.bind(this);
        this.handleNameChange = this.handleNameChange.bind(this);
    }

    componentDidMount() {
        const channel = this.channel();
        if (isPaidReconnectChannel(channel)) recordReconnectEvent('modal_shown', channel);
    }

    channel() {
        const { adAccountData } = this.props;
        return adAccountData ? adAccountData.channel : null;
    }

    isNewFlow() {
        return isPaidReconnectChannel(this.channel());
    }

    handleNameChange(e) {
        this.setState({ credentialName: e.target.value });
    }

    goToAuthUrl(path) {
        new APIClient(API_URL + path).call('GET')
            .then((data) => {
                if (!data || !data.auth_url) {
                    this.failAuthUrl();
                    return;
                }
                window.location = data.auth_url;
            })
            .catch(() => this.failAuthUrl());
    }

    // No se fue a la plataforma: la nota que se acaba de dejar no espera a ningún callback.
    failAuthUrl() {
        forgetReconnect();
        this.setState({ step: STEP_ERROR, errorMessage: this.props.t('reconnect_modal.error_auth_url') });
    }

    handleConnectNew() {
        const { adAccountData } = this.props;
        const channel = adAccountData.channel;
        const name = this.state.credentialName.trim();

        // Mercado Ads se resuelve en Conexiones: ahí su alta ofrece mandarle el link al vendedor (casi
        // siempre autoriza él) o conectar con la cuenta principal, y la fila cortada dice cuál de los dos.
        if (channel === MERCADO_ADS_CHANNEL) {
            window.location = `/agency/conections?add=${MERCADO_ADS_CHANNEL}`;
            return;
        }

        // Vacío = no se manda nombre: el backend usa el del usuario que entra. Un nombre que quedó de
        // otra conexión abandonada tampoco puede viajar.
        try {
            if (name) localStorage.setItem('pending_credential_label', name);
            else localStorage.removeItem('pending_credential_label');
        } catch (error) {
            // Sin localStorage la conexión sale con el nombre por default.
        }

        if (this.isNewFlow()) {
            recordReconnectEvent('modal_submit', channel);
            rememberReconnect(channel, { adAccountId: adAccountData.ad_account_id });
        } else {
            // GA4 no lo lee: una nota suya se la llevaría el próximo OAuth de Google.
            forgetReconnect();
        }

        if (OAUTH_AUTH_URL_BY_CHANNEL[channel]) {
            this.goToAuthUrl(OAUTH_AUTH_URL_BY_CHANNEL[channel]);
        } else if (channel === 'TIKTOK') {
            const redirectUri = encodeURIComponent(`${APP_URL}connect-tiktok`);
            window.location = `https://business-api.tiktok.com/portal/auth?app_id=${TIKTOK_APP_ID}&state=your_custom_params&redirect_uri=${redirectUri}`;
        } else if (channel === 'LINKEDIN') {
            // LinkedIn lleva nombre y cuenta en su nota de sessionStorage (15 min, un uso), escrita
            // entera: una reconexión abandonada no puede colarse. Lo de localStorage no lo usa.
            localStorage.removeItem('pending_credential_label');
            forgetReconnect();
            setPendingLinkedinConnection({
                label: name || null,
                adAccountId: adAccountData.ad_account_id,
            });
            window.location = LINKEDIN_AUTHORIZATION_URL;
        } else if (channel === 'FACEBOOK') {
            // Meta conecta con el popup de su SDK, que vive en el modal de Conexiones: la cuenta viaja
            // en la nota y la lee `FacebookLink` al autorizar.
            window.location = '/agency/conections?reconnect_pending=FACEBOOK';
        }
    }

    handleRetry() {
        this.setState({ step: STEP_SELECT, errorMessage: null });
    }

    /** «Qué pasó» del modal nuevo: el motivo y, si se sabe, quién ve la cuenta. */
    whatHappened(errorKind) {
        const { t, i18n, adAccountData } = this.props;
        let reason;
        if (LOST_ACCESS_KINDS.includes(errorKind)) {
            const date = formatShortDate(adAccountData.no_access_since_at, i18n && i18n.language);
            reason = date
                ? t('reconnect_modal.lost_since', { date, interpolation: RAW })
                : t('reconnect_modal.lost_undated');
        } else {
            reason = t('reconnect_modal.error_kind.' + errorKind);
        }
        const owner = ownerSentence(t, adAccountData.channel, adAccountData);
        return owner ? `${reason} ${owner}` : reason;
    }

    renderNewFlow(errorKind) {
        const { t, adAccountData } = this.props;
        const { credentialName } = this.state;
        const platform = reconnectPlatformName(adAccountData.channel);
        const isFacebookSecurityCheck = errorKind === ERROR_KIND_FB_SECURITY_CHECK;
        // Una cuenta cerrada por la plataforma no se arregla reconectando (P-5): su motivo dice qué hacer,
        // y «Entra a … con un usuario que vea esta cuenta» diría lo contrario. Reconectar queda como salida
        // secundaria, como en el checkpoint de Meta.
        const isAccountDisabled = errorKind === ERROR_KIND_ACCOUNT_DISABLED;
        const steps = isFacebookSecurityCheck || isAccountDisabled ? null : [
            t('reconnect_modal.what_to_do', { platform, interpolation: RAW }),
            ownerNamesPerson(adAccountData) ? t('reconnect_modal.person_gone', { platform, interpolation: RAW }) : null,
        ].filter(Boolean).join(' ');

        return (
            <>
                <ModalBody>
                    <div className="modal-section reconnect-account-modal">
                        <div className="reconnect-account-modal__what" data-testid="reconnect-what">
                            {this.whatHappened(errorKind)}
                        </div>
                        {steps && <p className="reconnect-account-modal__steps">{steps}</p>}
                        <FormGroup className="reconnect-account-modal__name">
                            <Label for="reconnect-account-name">{t('reconnect_modal.name_label')}</Label>
                            <Input
                                id="reconnect-account-name"
                                type="text"
                                value={credentialName}
                                onChange={this.handleNameChange}
                                placeholder={t('reconnect_modal.name_placeholder', { platform, interpolation: RAW })}
                            />
                            <FormText>{t('reconnect_modal.name_hint')}</FormText>
                        </FormGroup>
                    </div>
                </ModalBody>
                <ModalFooter className="reconnect-account-modal__foot">
                    <button className="btn -negative" onClick={this.props.onToggle}>
                        {t('reconnect_modal.cancel')}
                    </button>
                    {isFacebookSecurityCheck && (
                        <a className="btn -alt" href="https://www.facebook.com" target="_blank" rel="noopener noreferrer">
                            <FaIcon iconName="external-link" /> {t('reconnect_modal.go_to_facebook')}
                        </a>
                    )}
                    <button
                        className={isFacebookSecurityCheck || isAccountDisabled ? 'btn -alt -negative' : 'btn -alt'}
                        onClick={this.handleConnectNew}
                    >
                        {t('reconnect_modal.reconnect_with', { platform, interpolation: RAW })}
                    </button>
                </ModalFooter>
            </>
        );
    }

    renderSelect() {
        const { t } = this.props;
        const { credentialName } = this.state;
        const { adAccountData } = this.props;
        // Mostramos un mensaje amigable según el tipo de error (error_kind), nunca el detalle crudo de
        // la plataforma. Un kind ausente O DESCONOCIDO cae a un mensaje neutro (GENERIC): antes sólo
        // se cubría el ausente, así que un kind nuevo del backend imprimía la key de i18n sin traducir.
        const errorKind = resolveErrorKind(adAccountData && adAccountData.error_kind);
        if (this.isNewFlow()) return this.renderNewFlow(errorKind);

        const isMercadoAds = adAccountData && adAccountData.channel === MERCADO_ADS_CHANNEL;
        // Mercado Ads no pide nombre: la conexión se llama como el vendedor que autoriza.
        const isNameValid = isMercadoAds || credentialName.trim().length > 0;
        const currentCredentialLabel = adAccountData && adAccountData.agency_channel_credential_label;
        const errorMessage = t('reconnect_modal.error_kind.' + errorKind);
        // El checkpoint de seguridad de Meta (subcode 459) NO se resuelve reconectando en Dashbo: el
        // usuario tiene que resolver la verificación en facebook.com y la conexión se restablece sola.
        const isFacebookSecurityCheck = errorKind === ERROR_KIND_FB_SECURITY_CHECK;

        return (
            <>
                <ModalBody>
                    <div className="modal-section">
                        <div style={{
                            background: '#FEF2F2',
                            border: '1px solid #FCA5A5',
                            borderRadius: '0.3rem',
                            padding: '0.75em 1em',
                            marginBottom: '1em',
                            fontSize: '0.875rem',
                        }}>
                            {currentCredentialLabel && (
                                <div style={{ marginBottom: '0.5em' }}>
                                    <strong>{t('reconnect_modal.current_credential_label')}</strong>{' '}
                                    {currentCredentialLabel}
                                </div>
                            )}
                            <div>{errorMessage}</div>
                        </div>
                        {!isFacebookSecurityCheck && !isMercadoAds && <p>{t('reconnect_modal.description')}</p>}
                        {isMercadoAds && <p>{t('connections.mercado_ads.reconnect_from_connections')}</p>}
                        {!isMercadoAds && <FormGroup style={{ marginTop: '1em' }}>
                            <Label>{t('add_credential_modal.name_label')}</Label>
                            <Input
                                type="text"
                                value={credentialName}
                                onChange={this.handleNameChange}
                                placeholder={t('add_credential_modal.name_placeholder')}
                            />
                            <FormText>{t('add_credential_modal.name_hint')}</FormText>
                        </FormGroup>}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button className="btn -negative" onClick={this.props.onToggle}>
                        {t('reconnect_modal.cancel')}
                    </button>
                    <button
                        className="btn -alt"
                        onClick={this.handleConnectNew}
                        disabled={!isNameValid}
                    >
                        <FaIcon iconName="plus" /> {t(isMercadoAds ? 'connections.mercado_ads.go_to_connections' : 'reconnect_modal.connect_new')}
                    </button>
                </ModalFooter>
            </>
        );
    }

    renderError() {
        const { t } = this.props;
        const { errorMessage } = this.state;
        return (
            <>
                <ModalBody>
                    <div className="modal-section" style={{ textAlign: 'center', padding: '2em' }}>
                        <FaIcon iconName="exclamation-triangle" style={{ color: '#DF0D00', fontSize: '3em' }} />
                        <p style={{ marginTop: '1em', fontWeight: '600' }}>{t('reconnect_modal.error_no_access')}</p>
                        <p style={{ color: '#666' }}>{errorMessage}</p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button className="btn -negative" onClick={this.props.onToggle}>
                        {t('reconnect_modal.cancel')}
                    </button>
                    <button className="btn -alt" onClick={this.handleRetry}>
                        {t('reconnect_modal.retry')}
                    </button>
                </ModalFooter>
            </>
        );
    }

    render() {
        const { step } = this.state;

        switch (step) {
            case STEP_SELECT:
                return this.renderSelect();
            case STEP_ERROR:
                return this.renderError();
            default:
                return null;
        }
    }
}

export const PaidMediaReconnectFlow = withTranslation()(PaidMediaReconnectFlowClass);
